/** @format */

import { lazy, type ComponentType, type LazyExoticComponent } from 'react'

// Shared sessionStorage key with the 4-layer chunk-reload handler in src/main.tsx.
// Both use the same key so that a reload triggered by lazyWithRetry is observed by
// the main-thread handler's debounce window, preventing double reloads.
const RELOAD_KEY = 'chunk-reload-last-at'
const DEBOUNCE_MS = 10_000

type Importer<T extends ComponentType<any>> = () => Promise<{ default: T }>

/**
 * Wraps React.lazy() with deployment-aware retry logic.
 *
 * When a dynamic import fails (typically because stale index.html references
 * chunk hashes that no longer exist on the server after a deploy), this reloads
 * the page once. If a reload was already attempted within the debounce window,
 * the error is rethrown so the surrounding ErrorBoundary catches it gracefully
 * instead of entering an infinite reload loop.
 */
export function lazyWithRetry<T extends ComponentType<any>>(
  importer: Importer<T>,
): LazyExoticComponent<T> {
  return lazy(() =>
    importer().catch((error: unknown) => {
      const now = Date.now()
      const lastAt = Number(sessionStorage.getItem(RELOAD_KEY) ?? 0)

      if (now - lastAt > DEBOUNCE_MS) {
        sessionStorage.setItem(RELOAD_KEY, String(now))
        window.location.reload()
        // Return a never-resolving promise so React keeps the Suspense
        // fallback visible until the reload completes — avoids a flash of
        // ErrorBoundary fallback during the reload.
        return new Promise<{ default: T }>(() => {})
      }

      // Already retried recently — let ErrorBoundary handle it.
      throw error
    }),
  )
}
